import React, { useEffect, useState } from 'react';
import { bootPathname, bootSearchParams, updateBootSearchParams } from '../../../../config/boot/bootUrl';

import { EntryPanel } from './EntryPanel';
import { EntryBuildStatus } from './EntryBuildStatus';
import { TomeButton } from '../../primitives/tome';
interface AuthScreenProps {
  onNext: (session: { token: string; username: string; autoSelect?: boolean }) => void;
  /** M6 — Guest play callback. When provided, renders a "Play as Guest" button. */
  onGuestPlay?: () => void;
  /** M6 — Guest play error message (shown inline if set). */
  guestError?: string;
  /** M6 — Guest play loading state (disables button while requesting). */
  guestLoading?: boolean;
}

interface TwitchSessionInfo {
  twitchUserId: string;
  twitchLogin: string;
  twitchDisplayName: string;
  email: string | null;
}

/**
 * AuthScreen — Twitch-only login surface.
 *
 * The bcrypt /register + /login paths are dormant (Arc C.1, 2026-05-02);
 * the 20-account quick-login grid was removed 2026-05-02 along with the
 * legacy disclosure section. Twitch is the single canonical sign-in
 * path on this screen.
 *
 * On every non-prod tier (PTR, localhost, QA tunnels) `DevConfig.skipLogin`
 * is true by default, so this screen rarely renders for normal play —
 * `useAuth` mints a dev JWT via `/api/auth/dev/issue` and proceeds
 * straight into character_select. The screen exists for:
 *   - public-prod sign-ins, and
 *   - the `/diagnostic` admin surface, where Twitch identity is the
 *     gate for admin-only tabs (Mongo dump / drain / wipe-data).
 *
 * `onNext` is unused on this screen because Twitch sign-in completes
 * via OAuth redirect → server-side cookie set, not via in-screen state.
 * After redirect-back, the parent flow (PreGameFlow) re-mounts and
 * `useAuth` picks up the session via `/api/auth/twitch/session`.
 */
export const AuthScreen: React.FC<AuthScreenProps> = ({ onNext: _onNext, onGuestPlay, guestError, guestLoading }) => {
  const [twitchSession, setTwitchSession] = useState<TwitchSessionInfo | null>(null);
  const [twitchProbing, setTwitchProbing] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/twitch/session', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data?.authenticated) {
          setTwitchSession({
            twitchUserId: data.twitchUserId,
            twitchLogin: data.twitchLogin,
            twitchDisplayName: data.twitchDisplayName,
            email: data.email,
          });
        }
      })
      .catch(() => { /* network down / 401 — silent */ })
      .finally(() => { if (!cancelled) setTwitchProbing(false); });

    // Clean up the ?twitch=ok / ?twitch=error breadcrumb left by the OAuth
    // callback so the user doesn't accidentally bookmark it.
    try {
      if (bootSearchParams().has('twitch')) {
        updateBootSearchParams((params) => {
          params.delete('twitch');
          params.delete('reason');
        });
      }
    } catch { /* SSR / file:// — no-op */ }

    return () => { cancelled = true; };
  }, []);

  const handleTwitchSignIn = () => {
    // Carry the originating mode path (e.g. /gmrace) so the OAuth callback
    // lands back in this funnel instead of the bare landing. The server
    // sanitizes to a single path segment — bad values degrade to default.
    const path = bootPathname();
    const returnTo = path && path !== '/' ? `?returnTo=${encodeURIComponent(path)}` : '';
    window.location.href = `/api/auth/twitch/start${returnTo}`;
  };

  const handleTwitchSignOut = async () => {
    try {
      await fetch('/api/auth/twitch/logout', { method: 'POST', credentials: 'same-origin' });
    } catch { /* best-effort */ }
    setTwitchSession(null);
  };

  return <EntryPanel label="Sign in">
    <h1>{twitchSession ? `Welcome back, ${twitchSession.twitchDisplayName}` : 'Begin your journey'}</h1>
    <p className="entry-screen__lead">Sign in with Twitch to choose your heroes and continue their adventures.</p>
    {twitchSession ? <div className="entry-screen__well">
      <div className="entry-screen__label">Signed in with Twitch</div>
      <h2>{twitchSession.twitchDisplayName}</h2>
      <p>@{twitchSession.twitchLogin}</p>
      <TomeButton tone="secondary" cut="clean" onClick={handleTwitchSignOut} className="entry-screen__signout">Sign out of Twitch</TomeButton>
    </div> : <div className="entry-screen__actions">
      <TomeButton fullWidth cut="clean" disabled={twitchProbing} onClick={handleTwitchSignIn}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4.5 0 1 4.7v15.5h5.4V24h3l3.4-3.8h4.7L23 14.4V0H4.5zm15.6 13.5-3 3.3h-4.8l-3 3.3v-3.3H5.5V2.1h14.6v11.4zm-3.5-7.7v6.5h-2V5.8h2zm-5 0v6.5h-2V5.8h2z" /></svg>
        {twitchProbing ? 'Checking session…' : 'Sign in with Twitch'}
      </TomeButton>
      <p className="entry-screen__note">You’ll return here after signing in.</p>
    </div>}
    {onGuestPlay && !twitchSession && <>
      <hr className="entry-screen__divider" />
      <div className="entry-screen__label">Just visiting?</div>
      <TomeButton tone="secondary" cut="clean" fullWidth onClick={onGuestPlay} disabled={guestLoading || twitchProbing}>
        {guestLoading ? 'Joining…' : 'Play as Guest'}
      </TomeButton>
      <p className="entry-screen__note">Design your hero and play for 2 hours. Guest progress isn’t saved.</p>
      {guestError && <p role="alert" className="entry-screen__error">{guestError}</p>}
    </>}
    <div className="entry-screen__build"><EntryBuildStatus /></div>
  </EntryPanel>;
};
